import { useId, type ReactNode } from 'react';
import { Link } from 'react-router';
import { clsx } from '../../../ds/clsx';
import styles from './ChecklistTile.module.css';

/**
 * done: abgeblendet (opacity .72) · open: neutral · waiting: info icon (Domain) · blocked: warning border
 * (Widerrufsbelehrung) · next: accent border as the next action (Telefonie) · optional: dashed (Zahlungsmittel).
 */
export type ChecklistTileStatus = 'done' | 'open' | 'waiting' | 'blocked' | 'next' | 'optional';

export interface ChecklistTileAction {
  label: string;
  /** In-app link (every checklist action is one). */
  href: string;
}

export interface ChecklistTileProps {
  icon: ReactNode;
  title: string;
  text: string;
  status: ChecklistTileStatus;
  /** Line at the bottom of a tile without action („Erledigt 11:12", „USt-ID unbestätigt"). */
  meta?: string | null | undefined;
  action?: ChecklistTileAction | null | undefined;
}

/** One tile of the Admin 11 checklist grid. */
export function ChecklistTile({ icon, title, text, status, meta, action }: ChecklistTileProps) {
  const primary = status === 'blocked' || status === 'next';
  const titleId = useId();
  return (
    <article className={clsx(styles.tile, styles[status])} aria-labelledby={titleId}>
      <span className={styles.icon} aria-hidden="true">
        {icon}
      </span>
      <h3 id={titleId} className={styles.title}>
        {title}
      </h3>
      <p className={styles.text}>{text}</p>
      {action ? (
        <Link
          to={action.href}
          className={clsx(
            'pressable',
            styles.action,
            primary && styles.actionPrimary,
            status === 'optional' && styles.actionQuiet,
          )}
        >
          {action.label}
        </Link>
      ) : meta ? (
        <span className={styles.meta}>{meta}</span>
      ) : null}
    </article>
  );
}
